import { BreakpointObserver } from '@angular/cdk/layout';
import { CommonModule, DOCUMENT } from '@angular/common';
import { Component, computed, effect, inject, Renderer2, signal } from '@angular/core';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatSidenavModule } from '@angular/material/sidenav';
import { MatTooltipModule } from '@angular/material/tooltip';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { MarkdownModule } from 'ngx-markdown';

import { ContextualHelpService } from '../../../services/contextual-help.service';
import { HELP_INDEX, type HelpCategory, type HelpIndexEntry } from '../../../services/help-index';

const ROLE_KEY_PREFIX = 'role-';
const SIDE_MODE_MIN_WIDTH = '(min-width: 768px)';
const SPOTLIGHT_HOLD_MS = 3000;
const SPOTLIGHT_EXIT_MS = 400;
// Must match .help-sidenav's width in contextual-help-drawer.component.scss
const HELP_DRAWER_WIDTH_PX = 400;
const CATEGORY_LABEL_KEYS: Record<HelpCategory, string> = {
  pages: 'help.category.pages',
  contestOverview: 'help.category.contestOverview',
  scoring: 'help.category.scoring',
  roles: 'help.category.roles',
};

export interface HelpIndexGroup {
  category: HelpCategory;
  categoryLabelKey: string;
  entries: { entry: HelpIndexEntry; label: string }[];
}

@Component({
  selector: 'app-contextual-help-drawer',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatSidenavModule,
    MatButtonModule,
    MatButtonToggleModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatProgressSpinnerModule,
    MatTooltipModule,
    MarkdownModule,
    TranslocoPipe,
  ],
  templateUrl: './contextual-help-drawer.component.html',
  styleUrls: ['./contextual-help-drawer.component.scss'],
})
export class ContextualHelpDrawerComponent {
  readonly contextualHelpService = inject(ContextualHelpService);

  readonly isRoleFallback = computed(() =>
    this.contextualHelpService.currentHelpKey().startsWith(ROLE_KEY_PREFIX),
  );

  // Spotlight links only make sense when the doc being shown is the one that
  // actually matches the page underneath the drawer — otherwise there's nothing
  // on-screen for them to scroll to and highlight.
  readonly isSpotlightUsable = computed(
    () =>
      this.contextualHelpService.currentHelpKey() ===
      this.contextualHelpService.currentPageHelpKey(),
  );

  readonly isSideMode = signal(false);
  readonly isContentScrolled = signal(false);
  readonly isContentVisible = signal(true);
  readonly isBrowsing = signal(false);
  readonly searchQuery = signal('');

  // Exposed only for the debug footer, to see exactly which asset is being fetched.
  readonly activeLang = toSignal(inject(TranslocoService).langChanges$, {
    initialValue: inject(TranslocoService).getActiveLang(),
  });

  readonly currentIndexEntry = computed(() =>
    HELP_INDEX.find((entry) => entry.key === this.contextualHelpService.currentHelpKey()),
  );

  readonly breadcrumb = computed(() => {
    const entry = this.currentIndexEntry();
    if (!entry) return null;
    const label = this.translocoService.translate(entry.labelKey);
    const qualifier = entry.qualifierKey
      ? this.translocoService.translate(entry.qualifierKey)
      : null;
    return {
      category: this.translocoService.translate(CATEGORY_LABEL_KEYS[entry.category]),
      label: qualifier ? `${label} (${qualifier})` : label,
    };
  });

  readonly groupedIndex = computed<HelpIndexGroup[]>(() => {
    // Re-evaluate on language change too, since labels are translated.
    this.activeLang();
    const query = this.searchQuery().trim().toLowerCase();

    const matches = HELP_INDEX.map((entry) => {
      const label = this.translocoService.translate(entry.labelKey);
      const qualifier = entry.qualifierKey
        ? this.translocoService.translate(entry.qualifierKey)
        : '';
      const displayLabel = qualifier ? `${label} (${qualifier})` : label;
      return { entry, label: displayLabel };
    }).filter(
      ({ entry, label }) =>
        !query ||
        label.toLowerCase().includes(query) ||
        entry.key.toLowerCase().includes(query) ||
        entry.keywords?.some((keyword) => keyword.toLowerCase().includes(query)),
    );

    const groups: HelpIndexGroup[] = [];
    for (const match of matches) {
      let group = groups.find((g) => g.category === match.entry.category);
      if (!group) {
        group = {
          category: match.entry.category,
          categoryLabelKey: CATEGORY_LABEL_KEYS[match.entry.category],
          entries: [],
        };
        groups.push(group);
      }
      group.entries.push(match);
    }
    return groups;
  });

  private cleanupTimeout: ReturnType<typeof setTimeout> | undefined;
  private exitTimeout: ReturnType<typeof setTimeout> | undefined;
  private spotlightedElement: HTMLElement | undefined;
  private spotlightColumnOverlay: HTMLElement | undefined;

  private readonly document = inject(DOCUMENT);
  private readonly renderer = inject(Renderer2);
  private readonly breakpointObserver = inject(BreakpointObserver);
  private readonly translocoService = inject(TranslocoService);

  constructor() {
    this.breakpointObserver
      .observe(SIDE_MODE_MIN_WIDTH)
      .pipe(takeUntilDestroyed())
      .subscribe((state) => this.isSideMode.set(state.matches));

    // Fade the content back in whenever it changes (page switch, role toggle)
    effect(() => {
      this.contextualHelpService.content();
      this.isContentVisible.set(false);
      setTimeout(() => this.isContentVisible.set(true), 0);
    });

    // Report how much space the drawer is actually pushing content aside by, so
    // fixed-position elements (nav toolbar, env banner, page FABs) can shrink to
    // match instead of running full-width underneath it (or, for FABs sitting at
    // the right edge, ending up physically inside the drawer's own footprint).
    effect(() => {
      const inset =
        this.contextualHelpService.isOpen() && this.isSideMode() ? HELP_DRAWER_WIDTH_PX : 0;
      this.contextualHelpService.setContentInset(inset);
      this.document.documentElement.style.setProperty('--help-drawer-inset', `${inset}px`);
    });
  }

  onContentScroll(event: Event): void {
    this.isContentScrolled.set((event.target as HTMLElement).scrollTop > 4);
  }

  toggleBrowsing(): void {
    this.isBrowsing.update((value) => !value);
    if (!this.isBrowsing()) {
      this.searchQuery.set('');
    }
  }

  openFromIndex(key: string): void {
    this.contextualHelpService.open(key);
    this.isBrowsing.set(false);
    this.searchQuery.set('');
  }

  onMarkdownClick(event: Event): void {
    const target = event.target as HTMLElement;
    const anchor = target.closest('a');

    if (anchor) {
      const href = anchor.getAttribute('href');
      if (href && href.startsWith('#spotlight-')) {
        event.preventDefault();
        if (!this.isSpotlightUsable()) return;
        const elementId = href.substring(1); // Remove the '#'
        this.highlightElement(elementId);
      }
    }
  }

  private highlightElement(elementId: string): void {
    const targetElement = this.document.getElementById(elementId);
    if (!targetElement) return;

    // List-table column headers (e.g. a per-row status toggle or actions menu
    // column) mark themselves with data-spotlight-full-column: highlighting just
    // the header or one arbitrarily-chosen row reads as inconsistent when the
    // column's cells vary per row, so the whole column gets a single beam
    // instead of picking one element to ring-highlight.
    if (targetElement.hasAttribute('data-spotlight-full-column')) {
      this.highlightColumn(targetElement);
    } else {
      this.highlightSingleElement(targetElement);
    }
  }

  private highlightSingleElement(targetElement: HTMLElement): void {
    targetElement.scrollIntoView({ behavior: 'smooth', block: 'center' });

    this.clearActiveSpotlight();

    this.spotlightedElement = targetElement;
    this.renderer.addClass(targetElement, 'ui-spotlight-active');

    // Hold, then fade out gracefully instead of snapping back
    this.cleanupTimeout = setTimeout(() => {
      this.renderer.removeClass(targetElement, 'ui-spotlight-active');
      this.renderer.addClass(targetElement, 'ui-spotlight-exiting');

      this.exitTimeout = setTimeout(() => {
        this.renderer.removeClass(targetElement, 'ui-spotlight-exiting');
        this.spotlightedElement = undefined;
      }, SPOTLIGHT_EXIT_MS);
    }, SPOTLIGHT_HOLD_MS);
  }

  private highlightColumn(headerElement: HTMLElement): void {
    // Instant, not smooth: the overlay's position is measured right after this
    // call via getBoundingClientRect, which would be stale mid-scroll.
    headerElement.scrollIntoView({ behavior: 'auto', block: 'center' });

    const cellIndex = (headerElement as HTMLTableCellElement).cellIndex;
    const table = headerElement.closest('table');
    if (!table || cellIndex < 0) return;

    const bodyRows = Array.from(table.querySelectorAll<HTMLTableRowElement>('tr[mat-row]'));
    const lastCell = bodyRows[bodyRows.length - 1]?.cells[cellIndex];
    const headerRect = headerElement.getBoundingClientRect();
    const bottom = lastCell ? lastCell.getBoundingClientRect().bottom : headerRect.bottom;

    this.clearActiveSpotlight();

    const overlay = this.renderer.createElement('div') as HTMLElement;
    this.renderer.addClass(overlay, 'ui-spotlight-column-active');
    this.renderer.setStyle(overlay, 'top', `${headerRect.top}px`);
    this.renderer.setStyle(overlay, 'left', `${headerRect.left}px`);
    this.renderer.setStyle(overlay, 'width', `${headerRect.width}px`);
    this.renderer.setStyle(overlay, 'height', `${bottom - headerRect.top}px`);
    this.renderer.appendChild(this.document.body, overlay);
    this.spotlightColumnOverlay = overlay;

    this.cleanupTimeout = setTimeout(() => {
      this.renderer.removeClass(overlay, 'ui-spotlight-column-active');
      this.renderer.addClass(overlay, 'ui-spotlight-column-exiting');

      this.exitTimeout = setTimeout(() => {
        overlay.remove();
        if (this.spotlightColumnOverlay === overlay) {
          this.spotlightColumnOverlay = undefined;
        }
      }, SPOTLIGHT_EXIT_MS);
    }, SPOTLIGHT_HOLD_MS);
  }

  // Cancels whichever kind of spotlight (single element or column beam) is
  // currently in flight, so a new spotlight click never leaves a stale one
  // lingering or fighting over the shared timeouts.
  private clearActiveSpotlight(): void {
    if (this.cleanupTimeout) clearTimeout(this.cleanupTimeout);
    if (this.exitTimeout) clearTimeout(this.exitTimeout);

    if (this.spotlightedElement) {
      this.renderer.removeClass(this.spotlightedElement, 'ui-spotlight-active');
      this.renderer.removeClass(this.spotlightedElement, 'ui-spotlight-exiting');
      this.spotlightedElement = undefined;
    }
    if (this.spotlightColumnOverlay) {
      this.spotlightColumnOverlay.remove();
      this.spotlightColumnOverlay = undefined;
    }
  }
}
